import { MIDASProject } from '../../types/project.types';
import { ProjectState, DataSet, DatasetId, getFromDatasetRecord, createDatasetId } from '../../types';
import { createDataTableTab } from '../tab-system/TabRegistry';
import logger from './logger';

/**
 * プロジェクト状態の初期化
 * 
 * @param project - プロジェクトデータ
 * @returns 初期化されたプロジェクト状態
 */
export function initializeProjectState(project: MIDASProject): ProjectState {

  // プロジェクト状態が既に存在する場合はそれを返す
  if (project.projectState) {
    return project.projectState;
  }

  // プロジェクト状態が存在しない場合はデフォルトで初期化
  const datasetIds = Object.keys(project.datasets || {});
  const firstDatasetId = datasetIds.length > 0 ? datasetIds[0] : '';
  
  // 初期タブIDを取得（作成されたデータテーブルタブのID）
  let initialActiveTabId = '';
  if (firstDatasetId && project.datasets) {
    const firstDataset = project.datasets[createDatasetId(firstDatasetId)];
    const dataTableTab = createDataTableTab(createDatasetId(firstDatasetId), firstDataset.name);
    initialActiveTabId = dataTableTab.id;
  }
  
  const defaultProjectState: ProjectState = {
    datasetStates: {},
    viewState: {
      activeTab: initialActiveTabId
    },
    metadata: {
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      version: project.version || '0.1.0',
      description: project.metadata?.description
    }
  };

  // 各データセットの初期状態を設定
  if (project.datasets) {
    Object.keys(project.datasets).forEach(datasetId => {
      defaultProjectState.datasetStates[createDatasetId(datasetId)] = {
        filters: [],
        sorts: [],
        columnConfigurations: [],
        selectedColumns: [],
        selectedRows: [],
        viewState: {
          scrollPosition: { x: 0, y: 0 },
          columnWidths: {},
          hiddenColumns: []
        }
      };
    });
  }


  return defaultProjectState;
}

/**
 * アクティブなデータセットを取得
 * 
 * @param project - プロジェクト
 * @param activeDatasetId - アクティブなデータセットID（オプション）
 * @returns アクティブなデータセット、または最初のデータセット
 */
export function getActiveDataset(
  project: MIDASProject | null,
  activeDatasetId?: DatasetId | string
): DataSet | null {
  if (!project || !project.datasets) {
    return null;
  }
  
  // 指定されたIDのデータセットを返す
  if (activeDatasetId) {
    const datasetIdTyped = typeof activeDatasetId === 'string' ? createDatasetId(activeDatasetId) : activeDatasetId;
    const dataset = getFromDatasetRecord(project.datasets, datasetIdTyped);
    if (dataset) return dataset;
  }
  
  // プロジェクト状態からアクティブなデータセットIDを取得 (現在は使用されていない)
  // const stateActiveId = null; // project.projectState?.viewState?.activeDatasetId;
  // if (stateActiveId) {
  //   const dataset = getFromDatasetRecord(project.datasets, stateActiveId);
  //   if (dataset) return dataset;
  // }
  
  // フォールバック: 最初のデータセットを返す
  const datasetIds = Object.keys(project.datasets) as DatasetId[];
  if (datasetIds.length > 0) {
    const dataset = getFromDatasetRecord(project.datasets, datasetIds[0]);
    if (dataset) return dataset;
  }
  
  return null;
}

/**
 * プロジェクトの更新をマージ
 * 
 * @param current - 現在のプロジェクト
 * @param updates - 更新内容
 * @returns マージされたプロジェクト
 */
export function mergeProjectUpdates(
  current: MIDASProject,
  updates: Partial<MIDASProject>
): MIDASProject {
  return {
    ...current,
    ...updates,
    // ネストされたオブジェクトは深くマージ
    projectState: updates.projectState ? {
      ...current.projectState,
      ...updates.projectState,
      viewState: updates.projectState.viewState ? {
        ...current.projectState?.viewState,
        ...updates.projectState.viewState
      } : current.projectState?.viewState,
      datasetStates: updates.projectState.datasetStates ? {
        ...current.projectState?.datasetStates,
        ...updates.projectState.datasetStates
      } : current.projectState?.datasetStates
    } : current.projectState,
    datasets: updates.datasets ? {
      ...current.datasets,
      ...updates.datasets
    } : current.datasets,
    reports: updates.reports !== undefined ? updates.reports : current.reports
  };
}

/**
 * プロジェクトが有効かどうかをチェック
 * 
 * @param project - チェックするプロジェクト
 * @returns プロジェクトが有効な場合はtrue
 */
export function validateProject(project: MIDASProject | null): boolean {
  if (!project) return false;
  
  // 必須フィールドのチェック
  if (!project.id || !project.projectName) {
    logger.warn('[projectUtils] プロジェクトの必須フィールドが不足:', {
      hasId: !!project.id,
      hasName: !!project.projectName
    });
    return false;
  }
  
  // データセットのチェック（少なくとも1つは必要）
  const datasetCount = Object.keys(project.datasets || {}).length;
  if (datasetCount === 0) {
    logger.warn('[projectUtils] プロジェクトにデータセットがありません');
    return false;
  }
  
  return true;
}

/**
 * データセットの選択状態を取得
 * 
 * @param project - プロジェクト
 * @param datasetId - データセットID
 * @returns 選択状態
 */
export function getDatasetSelection(
  project: MIDASProject | null,
  datasetId: DatasetId | string
): { selectedColumns: string[]; selectedRows: number[] } {
  const defaultSelection = { selectedColumns: [], selectedRows: [] };
  
  if (!project?.projectState?.datasetStates) {
    return defaultSelection;
  }
  
  const datasetIdTyped = typeof datasetId === 'string' ? createDatasetId(datasetId) : datasetId;
  const datasetState = project.projectState.datasetStates[datasetIdTyped];
  if (!datasetState) {
    return defaultSelection;
  }
  
  return {
    selectedColumns: datasetState.selectedColumns || [],
    selectedRows: datasetState.selectedRows || []
  };
}

/**
 * プロジェクトのエクスポート用データを準備
 * 
 * @param project - エクスポートするプロジェクト
 * @returns エクスポート用に整形されたプロジェクトデータ
 */
export function prepareProjectForExport(project: MIDASProject): MIDASProject {
  // 不要なデータや循環参照を除去
  const exportProject = { ...project };
  
  // タイムスタンプを更新
  exportProject.metadata.lastModified = new Date().toISOString();
  
  // 一時的なデータを除去（必要に応じて追加）
  // 例: キャッシュされたデータ、一時的な選択状態など
  
  return exportProject;
}